// src/services/storage/redacaoStorageService.js

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system/legacy';
import { logger } from '../../utils/logger';

const REDACAO_HISTORICO_KEY = '@quizflow:redacao_historico';
const MAX_HISTORICO = 50; // Máximo de redações salvas
const PDF_DIR = `${FileSystem.documentDirectory}redacoes/`;

/**
 * Persiste o PDF da correção como arquivo e retorna a URI.
 * O base64 NUNCA vai pro AsyncStorage (50 itens estourariam o limite do Android).
 */
const salvarPdfDaCorrecao = async (id, pdfBase64) => {
    try {
        await FileSystem.makeDirectoryAsync(PDF_DIR, { intermediates: true });
        const pdfUri = `${PDF_DIR}${id}.pdf`;
        await FileSystem.writeAsStringAsync(pdfUri, pdfBase64, {
            encoding: FileSystem.EncodingType.Base64,
        });
        return pdfUri;
    } catch (error) {
        // PDF é enriquecimento — falha não pode impedir de salvar a correção
        logger.error('Erro ao salvar PDF da correção:', error);
        return null;
    }
};

const removerPdfDaCorrecao = async (pdfUri) => {
    if (!pdfUri) return;
    try {
        await FileSystem.deleteAsync(pdfUri, { idempotent: true });
    } catch (error) {
        logger.error('Erro ao remover PDF da correção:', error);
    }
};

/**
 * Salva uma redação corrigida no histórico
 * @param {Object} redacao - Dados da redação corrigida
 * @returns {Promise<Object>} - Redação salva com ID
 */
export const salvarRedacaoNoHistorico = async (redacao) => {
    try {
        const historico = await getHistoricoRedacoes();

        const { pdfBase64, ...dados } = redacao;
        const id = `redacao_${Date.now()}`;

        const novaRedacao = {
            id,
            dataCriacao: new Date().toISOString(),
            ...dados,
            pdfUri: pdfBase64 ? await salvarPdfDaCorrecao(id, pdfBase64) : null,
        };

        // Adiciona no início e limita o tamanho
        const novoHistorico = [novaRedacao, ...historico].slice(0, MAX_HISTORICO);

        // Apagar PDFs das redações que saíram pelo limite (FIFO)
        for (const removida of historico.slice(MAX_HISTORICO - 1)) {
            await removerPdfDaCorrecao(removida.pdfUri);
        }

        await AsyncStorage.setItem(REDACAO_HISTORICO_KEY, JSON.stringify(novoHistorico));

        return novaRedacao;
    } catch (error) {
        logger.error('Erro ao salvar redação no histórico:', error);
        throw error;
    }
};

/**
 * Obtém o histórico de redações corrigidas
 * @returns {Promise<Array>}
 */
export const getHistoricoRedacoes = async () => {
    try {
        const data = await AsyncStorage.getItem(REDACAO_HISTORICO_KEY);
        return data ? JSON.parse(data) : [];
    } catch (error) {
        logger.error('Erro ao obter histórico de redações:', error);
        return [];
    }
};

/**
 * Obtém uma redação específica por ID
 * @param {string} id - ID da redação
 * @returns {Promise<Object|null>}
 */
export const getRedacaoPorId = async (id) => {
    try {
        const historico = await getHistoricoRedacoes();
        return historico.find(r => r.id === id) || null;
    } catch (error) {
        logger.error('Erro ao obter redação por ID:', error);
        return null;
    }
};

/**
 * Remove uma redação do histórico
 * @param {string} id - ID da redação
 * @returns {Promise<void>}
 */
export const removerRedacaoDoHistorico = async (id) => {
    try {
        const historico = await getHistoricoRedacoes();
        const removida = historico.find(r => r.id === id);
        const novoHistorico = historico.filter(r => r.id !== id);
        await AsyncStorage.setItem(REDACAO_HISTORICO_KEY, JSON.stringify(novoHistorico));
        await removerPdfDaCorrecao(removida?.pdfUri);
    } catch (error) {
        logger.error('Erro ao remover redação do histórico:', error);
        throw error;
    }
};

/**
 * Obtém estatísticas do histórico
 * @returns {Promise<Object>}
 */
export const getEstatisticasHistorico = async () => {
    try {
        const historico = await getHistoricoRedacoes();

        if (historico.length === 0) {
            return {
                totalRedacoes: 0,
                mediaNotas: 0,
                melhorNota: 0,
                piorNota: 0,
                evolucao: [],
            };
        }

        const notas = historico.map(r => r.nota?.total || 0);

        return {
            totalRedacoes: historico.length,
            mediaNotas: Math.round(notas.reduce((a, b) => a + b, 0) / notas.length),
            melhorNota: Math.max(...notas),
            piorNota: Math.min(...notas),
            evolucao: historico.slice(0, 10).map(r => ({
                data: r.dataCriacao,
                nota: r.nota?.total || 0,
            })).reverse(),
        };
    } catch (error) {
        logger.error('Erro ao obter estatísticas do histórico:', error);
        return null;
    }
};
